// src/context/AuthContext.js
import { createContext, useState, useEffect, useCallback } from 'react';
import API_BASE_URL from '../config';
import {
  saveToken,
  getToken,
  isTokenExpired,
  getRefreshToken,
  setRememberedEmail
} from '../utils/tokenUtils';
import { subscribeToPush, subscribeToPushIfNeeded } from '../utils/pushNotifications';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initializeAuth = async () => {
      try {

        const storedUser = localStorage.getItem('fitscan_user');
        const token = getToken();

        if (storedUser && token) {
          const parsedUser = JSON.parse(storedUser);

          // Admin sessions must never bleed into AuthContext
          if (parsedUser.role === 'super_admin') {
            localStorage.removeItem('fitscan_user');
            localStorage.removeItem('accessToken');
            setUser(null);
            setLoading(false);
            return;
          }

          if (!isTokenExpired()) {
            // Token is fresh — restore immediately
            setUser(parsedUser);
            subscribeToPushIfNeeded();
          } else {
            // Token is stale but we have a user — try a silent refresh
            // before giving up on the session
            try {
              const refreshToken = getRefreshToken();
              if (refreshToken) {
                const resp = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ accessToken: token, refreshToken })
                });
                if (resp.ok) {
                  const data = await resp.json();
                  saveToken(data.token, data.refreshToken, 7);
                  // NEW: carry the auto-logout flag forward so the dashboard can show the banner
                  if (data.wasAutoLoggedOut === true) {
                    localStorage.setItem('fitscan_auto_logout_pending', 'true');
                  }
                  setUser(parsedUser);
                  subscribeToPushIfNeeded();
                } else {
                  // Refresh truly failed — clear and go to login
                  localStorage.removeItem('fitscan_user');
                  localStorage.removeItem('accessToken');
                  setUser(null);
                }
              } else {
                localStorage.removeItem('fitscan_user');
                localStorage.removeItem('accessToken');
                setUser(null);
              }
            } catch {
              // Network error during refresh — keep the user in state
              // so they stay logged in and can retry naturally
              setUser(parsedUser);
            }
          }
        } else {
          setUser(null);
        }
      } catch (err) {
        console.error('Auth initialization error:', err);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const login = (userData, rememberMe = false) => {
    if (!userData || !userData.user || !userData.token) {
      console.error('Invalid user data for login', userData);
      return;
    }
    setUser(userData.user);
    try {
      const expirationDays = rememberMe ? 30 : 7;
      saveToken(userData.token, userData.refreshToken, expirationDays);
      localStorage.setItem('fitscan_user', JSON.stringify(userData.user));

      // Auto-subscribe to push notifications upon login
      subscribeToPush();

      if (userData.wasAutoLoggedOut === true) {
        localStorage.setItem('fitscan_auto_logout_pending', 'true');
      }

      if (rememberMe && userData.user) {
        const email = userData.user.email || userData.user.username || '';
        if (email) {
          setRememberedEmail(email);
        }
      } else {
        setRememberedEmail('');
      }
    } catch (err) {
      console.error('Failed to save user to localStorage', err);
    }
  };

  const logout = async () => {
    try {
      const tokenData = JSON.parse(localStorage.getItem('accessToken') || 'null');
      if (tokenData && tokenData.token && tokenData.refreshToken) {
        // Attempt to revoke the refresh token on the server
        await fetch(`${API_BASE_URL}/api/auth/revoke`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${tokenData.token}`
          },
          body: JSON.stringify({ refreshToken: tokenData.refreshToken })
        }).catch(err => console.error('Token revocation failed:', err));
      }
    } catch (err) {
      console.error('Error during logout revocation:', err);
    }

    setUser(null);
    try {
      localStorage.removeItem('fitscan_user');
      localStorage.removeItem('accessToken');
    } catch (err) {
      console.error('Failed to remove user from localStorage', err);
    }
  };

  const updateUser = useCallback((fields) => {
    setUser(prev => {
      if (!prev) return prev;
      const updated = { ...prev, ...fields };
      localStorage.setItem('fitscan_user', JSON.stringify(updated));
      return updated;
    });
  }, []);

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
};
